/**
 * Страница «Мой прогресс» — `/progress` (FR-P3, docs/10-ui-ux.md).
 *
 * Без графиков: счётчики, разбивка по темам, трекам и сложности, последние
 * решённые.
 * Отдельно вынесен счётчик «решено самостоятельно» — это то, чем пользователю
 * приятно гордиться, и ради чего он отказывается от подсказки.
 *
 * Полосы прогресса рисуются шириной блока, а не картинкой: цвет берётся
 * из токенов темы и остаётся читаемым в обеих темах (NFR-U3).
 */

import { Link } from 'react-router'

import { useProgress, type DifficultyProgress } from '@/api/progress'
import { strings } from '@/lib/strings'

const DIFFICULTY_ORDER = ['easy', 'medium', 'hard'] as const

export function ProgressPage() {
  const { data, isPending, isError } = useProgress()

  if (isPending) {
    return <p className="text-sm text-muted">{strings.progress.loading}</p>
  }

  if (isError || data === undefined) {
    return (
      <p role="alert" className="text-sm text-warning">
        {strings.progress.loadFailed}
      </p>
    )
  }

  const { totals } = data

  return (
    <section aria-labelledby="progress-heading" className="space-y-6">
      <h1 id="progress-heading" className="text-2xl font-semibold tracking-tight">
        {strings.progress.title}
      </h1>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Counter
          value={strings.progress.solvedOfTotal(totals.solved, totals.tasks_total)}
          label={strings.catalog.status.solved}
        />
        <Counter
          value={String(totals.solved_without_hints)}
          label={strings.progress.solvedWithoutHints}
          accent
        />
        <Counter value={String(totals.review_solved)} label={strings.progress.reviewClosed} />
        <Counter value={String(totals.attempted)} label={strings.progress.attempted} />
      </div>

      {/* Секция идёт сразу после счётчиков, а не в конце страницы: это
          единственное место, где у человека есть незакрытая работа
          по уже решённым задачам (FR-P8). */}
      {data.review_debt.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted uppercase">
            {strings.progress.reviewSection(data.review_debt.length)}
          </h3>
          <ul className="space-y-1 text-sm">
            {data.review_debt.map((task) => (
              <li key={task.slug} className="flex items-baseline justify-between gap-2">
                <Link to={`/tasks/${task.slug}`} className="text-accent hover:underline">
                  {task.title}
                </Link>
                <span className="text-xs text-muted">
                  {strings.progress.reviewDueAt(formatDate(task.due_at))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-muted uppercase">{strings.progress.byTopic}</h3>
        <ul className="space-y-2">
          {data.by_topic.map((topic) => (
            <li key={topic.slug} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <Link to={`/tasks?topic=${topic.slug}`} className="hover:underline">
                  {topic.title}
                </Link>
                <span className="text-xs text-muted">
                  {strings.catalog.solvedOfTotal(topic.solved, topic.total)}
                </span>
              </div>
              <ProgressBar solved={topic.solved} total={topic.total} />
            </li>
          ))}
        </ul>
      </div>

      {data.by_track.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted uppercase">{strings.progress.byTrack}</h3>
          <ul className="space-y-2">
            {data.by_track.map((track) => (
              <li key={track.slug} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <Link to={`/tracks/${track.slug}`} className="hover:underline">
                    {track.title}
                  </Link>
                  <span className="text-xs text-muted">
                    {/* Ссылка ведёт прямо на первый нерешённый шаг: с прогресса
                        пользователь идёт работать, а не разглядывать список. */}
                    {track.next_task_slug === null ? (
                      strings.catalog.solvedOfTotal(track.solved, track.total)
                    ) : (
                      <Link
                        to={`/tasks/${track.next_task_slug}?track=${track.slug}`}
                        className="text-accent underline"
                      >
                        {strings.tracks.resume}
                      </Link>
                    )}
                  </span>
                </div>
                <ProgressBar solved={track.solved} total={track.total} />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-muted uppercase">
          {strings.progress.byDifficulty}
        </h3>
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
          {DIFFICULTY_ORDER.map((difficulty) => {
            const counts: DifficultyProgress = data.by_difficulty[difficulty] ?? {
              total: 0,
              solved: 0,
            }
            return (
              <div key={difficulty} className="contents">
                <dt className="text-muted">{strings.catalog.difficulty[difficulty]}</dt>
                <dd>{strings.catalog.solvedOfTotal(counts.solved, counts.total)}</dd>
              </div>
            )
          })}
        </dl>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-muted uppercase">
          {strings.progress.recentSolved}
        </h3>
        {data.recent_solved.length === 0 ? (
          <p className="text-sm text-muted">
            {strings.progress.recentEmpty}{' '}
            <Link to="/tasks" className="text-accent underline">
              {strings.progress.goToCatalog}
            </Link>
          </p>
        ) : (
          <ul className="space-y-1 text-sm">
            {data.recent_solved.map((task) => (
              <li key={task.slug} className="flex items-baseline justify-between gap-2">
                <Link to={`/tasks/${task.slug}`} className="text-accent hover:underline">
                  {task.title}
                </Link>
                {task.solved_at !== null && (
                  <span className="text-xs text-muted">
                    {strings.progress.solvedAt(formatDate(task.solved_at))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function Counter({
  value,
  label,
  accent = false,
}: {
  value: string
  label: string
  accent?: boolean
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <p className={`text-lg font-semibold ${accent ? 'text-success' : ''}`}>{value}</p>
      <p className="text-xs text-muted">{label}</p>
    </div>
  )
}

function ProgressBar({ solved, total }: { solved: number; total: number }) {
  const percent = total === 0 ? 0 : Math.round((solved / total) * 100)
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={solved}
      className="h-1.5 overflow-hidden rounded-full bg-border"
    >
      <div className="h-full rounded-full bg-accent" style={{ width: `${percent}%` }} />
    </div>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}
